import React from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';

interface CookieConsentBannerProps {
    onAcceptAll: () => void;
    onRejectAll: () => void;
    onManage: () => void;
}

/**
 * Bottom cookie-consent banner. Shown only until the visitor decides (the
 * provider unmounts it after that). "Reject all" is given equal visual weight
 * to "Accept all" (ICO/PECR requirement that rejecting is as easy as
 * accepting); "Manage" opens the granular preferences modal.
 *
 * Surfaces/ink come from the global paper tokens, so dark mode needs no JS.
 */
const CookieConsentBanner: React.FC<CookieConsentBannerProps> = ({
    onAcceptAll,
    onRejectAll,
    onManage,
}) => {
    const reduceMotion = useReducedMotion();

    const banner = (
        <motion.div
            className="cookie-banner"
            role="region"
            aria-label="Cookie consent"
            initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={
                reduceMotion ? { duration: 0.12 } : { type: 'spring', stiffness: 380, damping: 32 }
            }
        >
            <div className="cookie-banner__text">
                <p className="cookie-banner__title">We use cookies</p>
                <p className="cookie-banner__body">
                    Some keep Comtev running (sign-in, security, your preferences). With your
                    okay, we also use optional cookies to understand usage and power live chat.
                    See our{' '}
                    <Link to="/privacy">Privacy &amp; Cookie Policy</Link>.
                </p>
            </div>
            <div className="cookie-banner__actions">
                <button
                    type="button"
                    className="btn-physical btn-physical-ghost btn-physical-sm cookie-banner__manage"
                    onClick={onManage}
                >
                    Manage
                </button>
                <button
                    type="button"
                    className="btn-physical btn-physical-secondary btn-physical-sm"
                    onClick={onRejectAll}
                >
                    Reject all
                </button>
                <button
                    type="button"
                    className="btn-physical btn-physical-primary btn-physical-sm"
                    onClick={onAcceptAll}
                >
                    Accept all
                </button>
            </div>
        </motion.div>
    );

    return createPortal(banner, document.body);
};

export default CookieConsentBanner;
